import React from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { Checkbox } from '../checkbox'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { TransitionUnorderedList, TransitionListItem } from '../list'
import { panelTitleCss, nullValueCss } from '../text'
import {
  filterSectionSpacing,
  emptyStateFilterI18nConfig,
  isEmptyStateFilter
} from './shared'
import { WithTooltip } from '../../design-system'
import { FilterConfig, Option, Subfilter } from '.'
import {
  CollapsiblePaginatedListManager,
  CollapsiblePaginatedListToggle,
  ShowLessLink
} from '../list/collapsible-pagniated-list-manager'
import { ContrastFormattedMessage } from '../../components/contrast-formatted-message'
import { OverflowDetector } from '../overflow-detector'
import { Element } from '../overflow-detector/overflow-detector'

interface OnOptionToggleProps {
  readonly groupID: string
  readonly optionID: string
}
interface OnGroupToggleProps {
  readonly groupID: string
  readonly optionsAvailableForSelection: string[]
}

interface FilterProps {
  readonly group: FilterConfig
  readonly selections: string[]
  readonly onGroupToggle: (props: OnGroupToggleProps) => void
  readonly onMultiGroupToggle: () => void
  readonly onOptionToggle: (props: OnOptionToggleProps) => void
  readonly searchText?: string
}

const listCss = css`
  list-style-type: none;
  padding-left: 15px;
  padding-right: 15px !important;
  margin-bottom: 0;

  &:first-child {
    padding-top: ${filterSectionSpacing}px;
  }
`

const optionsListCss = css`
  padding-left: 25px;
  margin-bottom: 0;
`

const toggleCss = css`
  margin-left: 51px;
`

const optionCss = css`
  max-width: 300px;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`

const optionTextContainerCss = css`
  max-width: 180px;
  display: block;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const noCountOptionCss = cx(nullValueCss, optionTextContainerCss)

const groupCheckboxCss = css`
  display: inline-flex;
  width: 100%;
  margin-bottom: 2px;
`

const showLessLinkCss = css`
  padding-bottom: 7px;
  margin-left: auto;
`

const optionLabelCss = css`
  margin-right: 5px;
`

const groupOptionCss = cx(
  optionCss,
  css`
    margin-bottom: 0;
    flex-direction: column;
    align-items: flex-start;
  `
)

const optionTextCss = css`
  line-height: normal;
`

const optionNonHighlightedCss = cx(
  optionTextCss,
  css`
    line-height: 1.4;
  `
)

const renderLabelContent = (
  label: string,
  optionToTooltipContent: FilterConfig['optionToTooltipContent'],
  option: Option,
  intl: IntlShape,
  LabelContent: (props) => JSX.Element,
  overflowDetectorElementRef?: (el: Element | null) => void
) => {
  return (
    <>
      <span className={optionLabelCss} data-e2e={`filter-option-${label}`}>
        {optionToTooltipContent ? (
          <WithTooltip
            tiny
            delayRender={false}
            placement="right"
            renderTooltipContent={() => {
              return optionToTooltipContent(option, intl)
            }}
          >
            {() => {
              return (
                <LabelContent
                  label={label}
                  overflowDetectorElementRef={overflowDetectorElementRef}
                />
              )
            }}
          </WithTooltip>
        ) : (
          <LabelContent
            label={label}
            overflowDetectorElementRef={overflowDetectorElementRef}
          />
        )}
      </span>
      <OptionCount
        count={option.count}
        dataE2e={`filter-option-count-${label}`}
      />
    </>
  )
}

function groupNameOnChange({
  group,
  onMultiGroupToggle,
  onGroupToggle
}: {
  group: FilterConfig
  onMultiGroupToggle: () => void
  onGroupToggle: (props: OnGroupToggleProps) => void
}) {
  return groupHasSubfilters(group)
    ? onMultiGroupToggle()
    : onGroupToggle({
        groupID: group.id,
        optionsAvailableForSelection: getEnabledOptions(group)
      })
}

function GroupName({
  group,
  onMultiGroupToggle,
  onGroupToggle,
  isGroupChecked,
  total,
  totalDisabled
}: GroupNameProps) {
  return group.groupName ? (
    <ContrastFormattedMessage {...group.groupName}>
      {(name) => {
        return (
          <Checkbox
            onChange={() =>
              groupNameOnChange({ group, onGroupToggle, onMultiGroupToggle })
            }
            value={isGroupChecked}
            disabled={total - totalDisabled === 0}
            label={name}
            renderLabelContent={(label) => {
              return <div className={panelTitleCss} children={label} />
            }}
          />
        )
      }}
    </ContrastFormattedMessage>
  ) : null
}

function GroupDisplayName({
  group,
  onMultiGroupToggle,
  onGroupToggle,
  isGroupChecked,
  total,
  totalDisabled
}: GroupNameProps) {
  return group.groupDisplayName ? (
    <Checkbox
      onChange={() =>
        groupNameOnChange({ group, onGroupToggle, onMultiGroupToggle })
      }
      value={isGroupChecked}
      disabled={total - totalDisabled === 0}
      label={group.groupDisplayName}
      renderLabelContent={(label) => {
        return <div className={panelTitleCss} children={label} />
      }}
    />
  ) : null
}

function HighlightedText({ string, searchText }) {
  if (string && string.match) {
    const matches = string.match(
      new RegExp('(.*?)(' + searchText + ')(.*)', 'i')
    )
    if (matches) {
      const [a, b, c] = matches.slice(1)
      return (
        <span className={optionTextCss}>
          {a}
          <strong>{b}</strong>
          {c}
        </span>
      )
    }
  }
  return <span className={optionTextCss}>{string}</span>
}

interface OptionCountProps {
  readonly count?: number
  readonly dataE2e: string
}
export function OptionCount({ count, dataE2e }: OptionCountProps) {
  if (count === null || typeof count === 'undefined') {
    return null
  }
  return count === 0 ? (
    <span data-e2e={dataE2e} className={nullValueCss} children={`(${count})`} />
  ) : (
    <span
      data-e2e={dataE2e}
      className={css`
        font-family: ${deprecatedTheme.fontFamilies.secondary};
        color: ${theme.color.neutral60};
      `}
    >
      {`(${count})`}
    </span>
  )
}

export const allSubfiltersAreSelected = (
  selections: string[],
  option: Option
): boolean => {
  if (optionHasSubfilters(option)) {
    const enabledSubfilters = getEnabledSubfilters(option)
    if (enabledSubfilters.length > 0) {
      const subfiltersID: string[] = enabledSubfilters.map(
        (subfilter) => subfilter.keycode
      )
      return subfiltersID.every((id) => selections.includes(id))
    }
  }
  return false
}

export const optionHasSubfilters = (option: Option): boolean => {
  return !!option?.key?.subfilters && !!option?.key?.subfiltersId
}

export const getEnabledSubfilters = (option: Option): Subfilter[] => {
  return option.key.subfilters.filter((subfilter) => subfilter.count > 0)
}

const getDiasbledSubfilters = (option: Option) => {
  return option.key.subfilters.filter((subfilter) => !subfilter.count)
}

export const countTotalFiltersDisabled = (options: Option[]): number => {
  let disabled = 0

  options.forEach((option) => {
    const disabledSubfiltersCount = optionHasSubfilters(option)
      ? getDiasbledSubfilters(option).length
      : 0
    disabled += disabledSubfiltersCount + !option.count
  })
  return disabled
}

export const countTotalFiltersEnabled = (options: Option[]): number => {
  let enabled = 0

  options.forEach((option) => {
    const enabledSubfiltersCount = optionHasSubfilters(option)
      ? getEnabledSubfilters(option).length
      : 0
    // parent filter is subtracted from the count because you cannot filter by the parent filter
    // the parent filter is just the sum of its subfilters
    const enabledSubfiltersCountWithoutParent =
      enabledSubfiltersCount > 0 ? enabledSubfiltersCount - 1 : 0
    enabled += option.count !== 0 ? 1 : 0
    enabled += enabledSubfiltersCountWithoutParent
  })
  return enabled
}

const groupHasSubfilters = (group: FilterConfig): boolean => {
  return group.options.some((option) => optionHasSubfilters(option))
}

const getEnabledOptions = (group: FilterConfig) => {
  return group.options
    .filter((option) => option.count !== 0)
    .map((option) => group.optionToID(option))
}
interface GroupNameProps {
  readonly group: FilterConfig
  readonly onGroupToggle: (props: OnGroupToggleProps) => void
  readonly onMultiGroupToggle: () => void
  readonly isGroupChecked: boolean
  readonly total: number
  readonly totalDisabled: number
}

export function Filter({
  group,
  selections,
  onMultiGroupToggle,
  onGroupToggle,
  onOptionToggle,
  searchText
}: FilterProps) {
  const intl = useIntl()

  const max = 5
  const increment = 20
  const totalDisabled =
    group && group.options && countTotalFiltersDisabled(group.options)
  const totalEnabled =
    group && group.options && countTotalFiltersEnabled(group.options)
  const total = totalDisabled + totalEnabled
  const isGroupChecked =
    selections &&
    selections.length > 0 &&
    selections.length === total - totalDisabled

  const sortedOptions = [...group.options].sort((a, b) =>
    group.optionToGroupIndex
      ? group.optionToGroupIndex(a) - group.optionToGroupIndex(b)
      : 0
  )

  return sortedOptions ? (
    <CollapsiblePaginatedListManager
      max={max}
      increment={increment}
      items={sortedOptions}
    >
      {({ visibleItems, isExpanded, onShowMore, onShowLess, showToggle }) => {
        return (
          <ul className={listCss}>
            <li className={groupOptionCss}>
              <div className={groupCheckboxCss}>
                <GroupDisplayName
                  group={group}
                  onMultiGroupToggle={onMultiGroupToggle}
                  isGroupChecked={isGroupChecked}
                  onGroupToggle={onGroupToggle}
                  total={total}
                  totalDisabled={totalDisabled}
                />
                <GroupName
                  group={group}
                  isGroupChecked={isGroupChecked}
                  onMultiGroupToggle={onMultiGroupToggle}
                  onGroupToggle={onGroupToggle}
                  total={total}
                  totalDisabled={totalDisabled}
                />
                {isExpanded && (
                  <div className={showLessLinkCss}>
                    <ShowLessLink onShowLess={onShowLess} />
                  </div>
                )}
              </div>
              <>
                <TransitionUnorderedList className={optionsListCss}>
                  {visibleItems.map((option, index, options) => {
                    const optionID = group.optionToID(option)
                    const optionStr = group.optionToString(option, intl)
                    const newGrouping =
                      index > 0 &&
                      group.optionToGroupIndex &&
                      group.optionToGroupIndex(option) !==
                        group.optionToGroupIndex(options[index - 1])
                    const { optionToTooltipContent } = group

                    function LabelContent({
                      label,
                      overflowDetectorElementRef
                    }: {
                      readonly label: string
                      readonly overflowDetectorElementRef?: (
                        el: Element | null
                      ) => void
                    }) {
                      if (isEmptyStateFilter(label)) {
                        return (
                          <FormattedMessage
                            id={emptyStateFilterI18nConfig[label]}
                          >
                            {(labelMessage) => (
                              <span
                                className={cx(
                                  css`
                                    font-style: italic;
                                    color: ${theme.color.neutral80};
                                  `,
                                  option.count === 0 && noCountOptionCss
                                )}
                              >
                                {searchText ? (
                                  <HighlightedText
                                    string={labelMessage}
                                    searchText={searchText}
                                  />
                                ) : (
                                  <span className={optionNonHighlightedCss}>
                                    {labelMessage}
                                  </span>
                                )}
                              </span>
                            )}
                          </FormattedMessage>
                        )
                      }

                      return (
                        <span
                          ref={overflowDetectorElementRef}
                          className={
                            option.count === 0
                              ? noCountOptionCss
                              : optionTextContainerCss
                          }
                        >
                          {searchText ? (
                            <HighlightedText
                              string={optionStr}
                              searchText={searchText}
                            />
                          ) : (
                            <span className={optionNonHighlightedCss}>
                              {label}
                            </span>
                          )}
                        </span>
                      )
                    }

                    return (
                      <TransitionListItem
                        key={optionID}
                        className={optionCss}
                        children={
                          <>
                            {newGrouping && (
                              <hr
                                className={css`
                                  margin-top: 16px;
                                  margin-bottom: 20px;
                                  margin-left: -2px;
                                  margin-right: -2px;
                                  width: 100%;
                                `}
                              />
                            )}

                            {!optionHasSubfilters(option) && (
                              <OverflowDetector>
                                {({
                                  doesOverflowParent,
                                  registerElement: overflowDetectorElementRef
                                }) => {
                                  const onChange = () => {
                                    onOptionToggle({
                                      groupID: group.id,
                                      optionID
                                    })
                                  }

                                  const labelContent = (label) =>
                                    renderLabelContent(
                                      label,
                                      optionToTooltipContent,
                                      option,
                                      intl,
                                      LabelContent,
                                      overflowDetectorElementRef
                                    )

                                  return doesOverflowParent() ? (
                                    <WithTooltip
                                      placement="top-start"
                                      renderTooltipContent={() => (
                                        <span>{optionStr}</span>
                                      )}
                                      fontSize={13}
                                      tooltipMaxWidth="500px"
                                      containerClassName={css`
                                        cursor: default;
                                      `}
                                      children={() => (
                                        <Checkbox
                                          disabled={option.count === 0}
                                          key={optionID}
                                          label={optionStr}
                                          value={selections.includes(optionID)}
                                          onChange={onChange}
                                          renderLabelContent={labelContent}
                                        />
                                      )}
                                    />
                                  ) : (
                                    <Checkbox
                                      disabled={option.count === 0}
                                      key={optionID}
                                      label={optionStr}
                                      value={selections.includes(optionID)}
                                      onChange={onChange}
                                      renderLabelContent={labelContent}
                                    />
                                  )
                                }}
                              </OverflowDetector>
                            )}

                            {optionHasSubfilters(option) && (
                              <>
                                <Checkbox
                                  disabled={option.count === 0}
                                  key={optionID}
                                  label={optionStr}
                                  value={allSubfiltersAreSelected(
                                    selections,
                                    option
                                  )}
                                  onChange={() => {
                                    const groupID = option.key.subfiltersId
                                    const optionsAvailableForSelection =
                                      getEnabledSubfilters(option).map(
                                        (enabledSubfilter) =>
                                          enabledSubfilter.keycode
                                      )
                                    onGroupToggle({
                                      groupID,
                                      optionsAvailableForSelection
                                    })
                                  }}
                                  renderLabelContent={(label) =>
                                    renderLabelContent(
                                      label,
                                      optionToTooltipContent,
                                      option,
                                      intl,
                                      LabelContent
                                    )
                                  }
                                />

                                <TransitionUnorderedList
                                  className={optionsListCss}
                                >
                                  {option.key.subfilters.map((subfilter) => {
                                    const subfiltersId = option.key.subfiltersId
                                    return (
                                      <TransitionListItem
                                        key={subfilter.keycode}
                                        className={optionCss}
                                        children={
                                          <>
                                            <Checkbox
                                              label={subfilter.label}
                                              disabled={subfilter.count === 0}
                                              value={selections.includes(
                                                subfilter.keycode
                                              )}
                                              onChange={() =>
                                                onOptionToggle({
                                                  groupID: subfiltersId,
                                                  optionID: subfilter.keycode
                                                })
                                              }
                                              renderLabelContent={(label) => {
                                                return (
                                                  <>
                                                    <span
                                                      className={optionLabelCss}
                                                    >
                                                      <LabelContent
                                                        label={label}
                                                      />
                                                    </span>
                                                    <OptionCount
                                                      count={subfilter.count}
                                                      dataE2e={`filter-option-count-${label}`}
                                                    />
                                                  </>
                                                )
                                              }}
                                            />
                                          </>
                                        }
                                      />
                                    )
                                  })}
                                </TransitionUnorderedList>
                              </>
                            )}
                          </>
                        }
                      />
                    )
                  })}
                </TransitionUnorderedList>
                {showToggle && (
                  <CollapsiblePaginatedListToggle
                    className={toggleCss}
                    totalItems={sortedOptions.length}
                    totalVisibleItems={visibleItems.length}
                    onShowLess={onShowLess}
                    onShowMore={onShowMore}
                  />
                )}
              </>
            </li>
          </ul>
        )
      }}
    </CollapsiblePaginatedListManager>
  ) : (
    <ul className={listCss} />
  )
}
